import { encryptData, decryptData, deriveKeyFromPassword } from "../utils/CryptoHelpers";
import { uint8ArraysEqual } from "../utils/CryptoHelpers";
import bs58 from 'bs58';
import { assertUint8Arrays } from './Utils';

async function getUserKey(userId, password) {
  const base64Salt = localStorage.getItem(`unlink_${userId}_salt`);
  if (!base64Salt) throw new Error("Missing salt for user");

  const salt = new Uint8Array(atob(base64Salt).split("").map(c => c.charCodeAt(0)));
  return await deriveKeyFromPassword(password, salt);
}

/**
 * Retrieves a specific contact by their identityViewPublicKey.
 *
 * @param {Object} params
 * @param {Uint8Array} params.identityViewPublicKey - The public key to look up.
 * @param {string} params.userId - The user ID.
 * @param {string} params.password - The user password.
 * @returns {Promise<Object|null>} The matching contact or null if not found.
 */
export async function getContact({ identityViewPublicKey, userId, password }) {
  const contacts = await loadContacts({ userId, password });

  const match = contacts.find((c) =>
    uint8ArraysEqual(c.identityViewPublicKey, identityViewPublicKey)
  );

  return match || null;
}

/**
 * Loads and decrypts the contact list for a given user.
 * Converts base58-encoded view keys back to Uint8Array format.
 *
 * @param {string} userId - The ID of the current user.
 * @param {string} password - The user's password for decryption.
 * @returns {Promise<Array>} Array of contacts with decoded identityViewPublicKey.
 */
export async function loadContacts({userId, password}) {
  const encrypted = localStorage.getItem(`unlink_${userId}_contacts`);
  if (!encrypted) return [];

  try {
    const parsed = JSON.parse(encrypted);
    const key = await getUserKey(userId, password);
    const decrypted = await decryptData(parsed, key);
    const contacts = JSON.parse(decrypted);

    return contacts.map((c) => ({
      ...c,
      identityViewPublicKey: bs58.decode(c.identityViewPublicKey),
    }));
  } catch (err) {
    console.error("Failed to load contacts", err);
    return [];
  }
}

/**
 * Encrypts and saves the user's contact list to localStorage.
 *
 * - All `identityViewPublicKey` fields must be provided as `Uint8Array`.
 * - Encodes `identityViewPublicKey` values as base58 strings before serialization.
 * - Uses the user's derived encryption key (from password + salt) to encrypt the list.
 * - Stores encrypted contacts in localStorage under the key: `unlink_${userId}_contacts`.
 *
 * @param {Array<Object>} contacts - Array of contact objects, each with a Uint8Array `identityViewPublicKey` and a `name`.
 * @param {string} userId - The user's ID (used to derive encryption key and storage key).
 * @param {string} password - The user's password (used to derive encryption key).
 * @returns {Promise<void>}
 */
async function saveContacts(contacts, userId, password) {
  const key = await getUserKey(userId, password);

  // Ensure all identityViewPublicKeys are encoded
  const serializableContacts = contacts.map(c => ({
    ...c,
    identityViewPublicKey: bs58.encode(c.identityViewPublicKey),
  }));

  const encrypted = await encryptData(JSON.stringify(serializableContacts), key);
  localStorage.setItem(`unlink_${userId}_contacts`, JSON.stringify(encrypted));
}

/**
 * Adds a new contact to the contact list if not already present.
 *
 * @param {Object} params
 * @param {Uint8Array} params.identityViewPublicKey - The contact's initial public view key.
 * @param {string} params.name - The display name of the contact.
 * @param {string} userId - The ID of the current user.
 * @param {string} password - The user's password for decryption.
 * @returns {Array|undefined} The updated contact list if a new contact was added, or undefined if it already existed.
 * @throws {Error} If inputs are not of expected types.
 */
export async function addContact({ 
  identityViewPublicKey,
  name,
  userId, 
  password,
}) {
  // Validate that all binary fields are Uint8Array
  assertUint8Arrays({ identityViewPublicKey });

  // Validate that name is a string
  if (typeof name !== 'string') {
    throw new Error("name must be a string");
  }

  // Load existing contacts from storage
  const contacts = await loadContacts({userId, password});

  // Check if contact already exists by comparing decoded keys
  const existing = contacts.find((c) => {
    try {
      return uint8ArraysEqual(c.identityViewPublicKey, identityViewPublicKey);
    } catch {
      return false;
    }
  });

  if (existing) {
    // Contact already exists, skip adding
    return;
  }

  // Add the new contact, encoding the view key as base58
  const updated = [...contacts, { 
    identityViewPublicKey: identityViewPublicKey,
    name,
  }];

  // Save the updated contact list
  await saveContacts(updated, userId, password);

  return updated;
}

export async function updateContact({
  identityViewPublicKey,
  name,
  userId,
  password,
}) { 
  assertUint8Arrays({ identityViewPublicKey });

  if (typeof name !== 'string') {
    throw new Error("name must be a string");
  }

  const contacts = await loadContacts({ userId, password });

  let found = false;

  const updatedContacts = contacts.map((c) => {
    if (uint8ArraysEqual(c.identityViewPublicKey, identityViewPublicKey)) {
      found = true;
      return {
        ...c,
        identityViewPublicKey: c.identityViewPublicKey,
        name,
      };
    }
    return c;
  });

  if (!found) {
    throw new Error("Contact not found");
  }

  await saveContacts(updatedContacts, userId, password);

  return updatedContacts;
}

export async function deleteContact({
    identityViewPublicKey,
    userId,
    password,
  }) {
  const contacts = await loadContacts({ userId, password });
  
  const updated = contacts.filter(
    (c) => !uint8ArraysEqual(c.identityViewPublicKey, identityViewPublicKey)
  );

  await saveContacts(updated, userId, password);
  return updated;
}